import { Button, ButtonVariant, Spinner, Text } from "@merge-api/merge-javascript-shared";
import { useState } from "react";
import {
  CommonModelDiffPhase,
  CommonModelDiffRunExecution,
  ViewExecutionLogsHandler,
} from "../entities";
import useAbortCommonModelDiffRun from "../hooks/useAbortCommonModelDiffRun";
import SubRunLogLinks from "./SubRunLogLinks";

interface AwaitingCompletionDiffCardContentProps {
  diffRun: CommonModelDiffRunExecution;
  setCommonModelDiffRunExecutions: (diffRuns: CommonModelDiffRunExecution[]) => void;
  commonModelDiffRunExecutions: CommonModelDiffRunExecution[];
  onViewExecutionLogs?: ViewExecutionLogsHandler;
}

// A running diff executes the published then the staged blueprint serially before diffing; the
// backend `phase` tells them apart (status alone is a flat PENDING for most of the run).
const PHASE_LABELS: Record<CommonModelDiffPhase, string> = {
  starting: "Starting",
  published_running: "Running published blueprint",
  published_done: "Published done — starting staged",
  staged_running: "Running staged blueprint",
  diffing: "Computing diff",
};

const AwaitingCompletionDiffCardContent = ({
  diffRun,
  setCommonModelDiffRunExecutions,
  commonModelDiffRunExecutions,
  onViewExecutionLogs,
}: AwaitingCompletionDiffCardContentProps) => {
  const [isAborting, setIsAborting] = useState(false);

  const { abortDiffRun } = useAbortCommonModelDiffRun({
    onSuccess: () => {
      setIsAborting(false);
      const filteredRuns = commonModelDiffRunExecutions.filter((run) => run.id !== diffRun.id);
      setCommonModelDiffRunExecutions(filteredRuns);
    },
  });

  const handleCancelRun = () => {
    setIsAborting(true);
    abortDiffRun(diffRun.id);
  };

  const phaseLabel = (diffRun.phase && PHASE_LABELS[diffRun.phase]) || "Running";

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row items-center space-x-2">
          <Spinner />
          <Text as="div" variant="md" className="text-gray-40">
            {phaseLabel} — {diffRun.linked_account_name}...
          </Text>
        </div>
        <Button
          onClick={handleCancelRun}
          size="sm"
          variant={ButtonVariant.DangerText}
          disabled={isAborting}
        >
          {isAborting ? "Cancelling..." : "Cancel run"}
        </Button>
      </div>
      <SubRunLogLinks diffRun={diffRun} onViewExecutionLogs={onViewExecutionLogs} />
    </div>
  );
};

export default AwaitingCompletionDiffCardContent;
